'use client';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-5 space-y-1">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

export function PrivacyPolicyContent() {
  const { t } = useTranslation();
  const p = (key: string) => t(`pages.privacyPolicy.${key}`);

  return (
    <div className="grid grid-cols-1 gap-5 lg:gap-7.5 mt-5">
      <Card>
        <CardHeader className="flex-col items-start py-5">
          <CardTitle as="h1" className="text-xl">{p('title')}</CardTitle>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-1">
            {p('effective')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">{p('subtitle')}</p>
        </CardHeader>
        <CardContent className="space-y-8">
          {/* 1. Controller */}
          <Section title={p('s1.title')}>
            <p>{p('s1.label')}</p>
            <p>
              Firmendata UG (haftungsbeschränkt)
              <br />
              c/o Ersin Bayraktar
              <br />
              Pistoriusstraße 102A
              <br />
              13086 Berlin
              <br />
              {p('s1.country')}
            </p>
            <p>
              <strong>{p('s1.representatives')}</strong> Julian Lenz, Ersin
              Bayraktar
            </p>
            <p>
              <strong>{p('s1.contact')}</strong>{' '}
              <a
                href="mailto:info@firmendata.com"
                className="text-primary hover:underline"
              >
                info@firmendata.com
              </a>
            </p>
          </Section>

          {/* 2. Data Protection Officer */}
          <Section title={p('s2.title')}>
            <p>{p('s2.body')}</p>
          </Section>

          {/* 3. Categories of Data About You */}
          <Section title={p('s3.title')}>
            <p>{p('s3.intro')}</p>
            <BulletList
              items={[
                p('s3.usage'),
                p('s3.account'),
                p('s3.contact'),
                p('s3.security'),
                p('s3.billing'),
                p('s3.apiAccess'),
                p('s3.watchlistData'),
                p('s3.webhookConfig'),
                p('s3.searchQuery'),
              ]}
            />
            <p>{p('s3.noSpecial')}</p>
          </Section>

          {/* 4. Personal Data About Persons in Public Registers */}
          <Section title={p('s4.title')}>
            <p>{p('s4.intro')}</p>
            <p>{p('s4.subjectsIntro')}</p>
            <BulletList
              items={[
                p('s4.subjectManagement'),
                p('s4.subjectShareholders'),
                p('s4.subjectUbo'),
                p('s4.subjectInsolvency'),
                p('s4.subjectTed'),
              ]}
            />
            <p>{p('s4.categoriesIntro')}</p>
            <BulletList
              items={[
                p('s4.categoryName'),
                p('s4.categoryRole'),
                p('s4.categoryAddress'),
                p('s4.categoryDob'),
                p('s4.categoryShareholding'),
              ]}
            />
            <p>{p('s4.sourcesIntro')}</p>
            <BulletList
              items={[
                p('s4.sourceHr'),
                p('s4.sourceBundesanzeiger'),
                p('s4.sourceLei'),
                p('s4.sourceTed'),
                p('s4.sourceInsolvency'),
              ]}
            />
            <p>{p('s4.legalBasis')}</p>
            <p>{p('s4.purposes')}</p>
            <p>{p('s4.art14')}</p>
            <p>{p('s4.rights')}</p>
            <p>{p('s4.objection')}</p>
            <p>{p('s4.rectification')}</p>
          </Section>

          {/* 5. Purposes and Legal Bases */}
          <Section title={p('s5.title')}>
            <p>{p('s5.intro')}</p>
            <BulletList
              items={[
                p('s5.website'),
                p('s5.services'),
                p('s5.subscriptionAdmin'),
                p('s5.apiOperation'),
                p('s5.inquiries'),
                p('s5.analytics'),
                p('s5.legal'),
              ]}
            />
            <p>{p('s5.withdraw')}</p>
          </Section>

          {/* 6. Cookies and Consent Management */}
          <Section title={p('s6.title')}>
            <p>{p('s6.body1')}</p>
            <p>{p('s6.body2')}</p>
          </Section>

          {/* 7. Hosting (Hetzner) */}
          <Section title={p('s7.title')}>
            <p>{p('s7.body')}</p>
            <BulletList
              items={[p('s7.purpose'), p('s7.legal'), p('s7.dpa')]}
            />
          </Section>

          {/* 8. CDN / Proxy (Cloudflare) */}
          <Section title={p('s8.title')}>
            <p>{p('s8.body')}</p>
            <BulletList
              items={[
                p('s8.purpose'),
                p('s8.legal'),
                p('s8.dpa'),
                p('s8.transfer'),
              ]}
            />
          </Section>

          {/* 9. Spam Prevention (Turnstile) */}
          <Section title={p('s9.title')}>
            <p>{p('s9.body')}</p>
            <BulletList
              items={[
                p('s9.purpose'),
                p('s9.legal'),
                p('s9.cookies'),
                p('s9.transfer'),
              ]}
            />
          </Section>

          {/* 10. Authentication (Auth0) */}
          <Section title={p('s10.title')}>
            <p>{p('s10.body')}</p>
            <BulletList
              items={[
                p('s10.name'),
                p('s10.email'),
                p('s10.signup'),
                p('s10.login'),
              ]}
            />
            <BulletList
              items={[
                p('s10.purpose'),
                p('s10.legal'),
                p('s10.dpa'),
                p('s10.transfer'),
              ]}
            />
          </Section>

          {/* 11. Payment Processing (Stripe) */}
          <Section title={p('s11.title')}>
            <p>{p('s11.body')}</p>
            <p>{p('s11.categories')}</p>
            <BulletList
              items={[
                p('s11.purpose'),
                p('s11.legal'),
                p('s11.dpa'),
                p('s11.transfer'),
              ]}
            />
            <p>{p('s11.ownPolicy')}</p>
            <p>{p('s11.tax')}</p>
          </Section>

          {/* 12. Transactional Email (Microsoft Azure Communication Services) */}
          <Section title={p('s12.title')}>
            <p>{p('s12.body')}</p>
            <p>{p('s12.emailsIntro')}</p>
            <BulletList
              items={[
                p('s12.emailAccount'),
                p('s12.emailBilling'),
                p('s12.emailWatchlist'),
                p('s12.emailWebhook'),
              ]}
            />
            <BulletList
              items={[
                p('s12.purpose'),
                p('s12.legal'),
                p('s12.dpa'),
                p('s12.transfer'),
              ]}
            />
          </Section>

          {/* 13. AI-Generated Insights */}
          <Section title={p('s13.title')}>
            <p>{p('s13.body')}</p>
            <p>{p('s13.noDecision')}</p>
            <p>{p('s13.providers')}</p>
            <p>{p('s13.transfer')}</p>
            <p>{p('s13.optOut')}</p>
          </Section>

          {/* 14. MCP Connector for Claude (Anthropic) */}
          <Section title={p('s14.title')}>
            <p>{p('s14.body')}</p>
            <p>{p('s14.howItWorks')}</p>
            <p>{p('s14.dataShared')}</p>
            <p>{p('s14.yourChoice')}</p>
            <p>{p('s14.anthropicNotice')}</p>
          </Section>

          {/* 15. Analytics and advertising (Google Analytics, Google Ads) */}
          <Section title={p('s15.title')}>
            <p>{p('s15.body')}</p>
            <p>{p('s15.noAds')}</p>
            <BulletList
              items={[
                p('s15.purpose'),
                p('s15.legal'),
                p('s15.consent'),
                p('s15.transfer'),
              ]}
            />
            <p className="font-medium">{p('s15.adsTitle')}</p>
            <p>{p('s15.adsBody')}</p>
            <BulletList
              items={[
                p('s15.adsPurpose'),
                p('s15.adsLegal'),
                p('s15.adsConsent'),
                p('s15.adsTransfer'),
              ]}
            />
          </Section>

          {/* 16. Contacting Us */}
          <Section title={p('s16.title')}>
            <p>{p('s16.body')}</p>
            <BulletList items={[p('s16.legal'), p('s16.retention')]} />
          </Section>

          {/* 17. Server Logs and Retention */}
          <Section title={p('s17.title')}>
            <p>{p('s17.body')}</p>
            <p>{p('s17.providers')}</p>
          </Section>

          {/* 18. Retention of Other Data */}
          <Section title={p('s18.title')}>
            <p>{p('s18.intro')}</p>
            <BulletList
              items={[
                p('s18.account'),
                p('s18.billing'),
                p('s18.subscription'),
                p('s18.watchlist'),
                p('s18.apiKeys'),
                p('s18.apiUsage'),
                p('s18.webhooks'),
                p('s18.contactEmail'),
                p('s18.registerData'),
              ]}
            />
          </Section>

          {/* 19. Recipients of Data */}
          <Section title={p('s19.title')}>
            <p>{p('s19.intro')}</p>
            <BulletList
              items={[
                p('s19.processors'),
                p('s19.authorities'),
                p('s19.users'),
              ]}
            />
            <p>{p('s19.noSale')}</p>
          </Section>

          {/* 20. International Data Transfers */}
          <Section title={p('s20.title')}>
            <p>{p('s20.body')}</p>
            <BulletList
              items={[p('s20.scc'), p('s20.measures'), p('s20.adequacy')]}
            />
          </Section>

          {/* 21. Your Rights */}
          <Section title={p('s21.title')}>
            <p>{p('s21.intro')}</p>
            <BulletList
              items={[
                p('s21.access'),
                p('s21.rectification'),
                p('s21.erasure'),
                p('s21.restriction'),
                p('s21.portability'),
                p('s21.object'),
                p('s21.withdraw'),
              ]}
            />
            <p>{p('s21.exercise')}</p>
            <p>{p('s21.deadline')}</p>
          </Section>

          {/* 22. Right to Lodge a Complaint */}
          <Section title={p('s22.title')}>
            <p>{p('s22.body')}</p>
            <p>{p('s22.berlin')}</p>
          </Section>

          {/* 23. Security Measures */}
          <Section title={p('s23.title')}>
            <p>{p('s23.body')}</p>
          </Section>

          {/* 24. Changes */}
          <Section title={p('s24.title')}>
            <p>{p('s24.body')}</p>
          </Section>

          {/* 25. Audience and Children */}
          <Section title={p('s25.title')}>
            <p>{p('s25.body')}</p>
            <p>{p('s25.note')}</p>
          </Section>
        </CardContent>
      </Card>
    </div>
  );
}
